/**
 * Analytics Service (Frontend)
 *
 * Handles API calls for creator analytics.
 */

import api from './api';

export interface CreatorOverview {
  totalStreams: number;
  totalPlays: number;
  totalFavorites: number;
  totalComments: number;
  totalShares: number;
  uniqueListeners: number;
  avgPlaysPerStream: number;
}

export interface TimeSeriesPoint {
  date: string;
  count: number;
}

export interface TopStream {
  streamId: string;
  name: string;
  coverImage?: string;
  playCount: number;
  favoriteCount: number;
  trend: number;
}

export interface PlaysResponse {
  startDate: string;
  endDate: string;
  data: TimeSeriesPoint[];
}

export interface ListenersResponse {
  startDate: string;
  endDate: string;
  data: TimeSeriesPoint[];
}

export interface StreamStats {
  totalPlays: number;
  uniqueListeners: number;
  avgDuration: number;
  playsByDate: TimeSeriesPoint[];
}

export const analyticsService = {
  /**
   * Get creator overview stats
   */
  async getOverview(): Promise<CreatorOverview> {
    return api.get<CreatorOverview>('/analytics/overview');
  },

  /**
   * Get plays by date (time series)
   */
  async getPlaysByDate(startDate?: string, endDate?: string): Promise<PlaysResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    const queryString = params.toString();
    return api.get<PlaysResponse>(`/analytics/plays${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get listener growth (time series)
   */
  async getListenerGrowth(startDate?: string, endDate?: string): Promise<ListenersResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('start', startDate);
    if (endDate) params.append('end', endDate);
    const queryString = params.toString();
    return api.get<ListenersResponse>(`/analytics/listeners${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get top streams
   */
  async getTopStreams(limit: number = 10): Promise<TopStream[]> {
    return api.get<TopStream[]>(`/analytics/top-streams?limit=${limit}`);
  },

  /**
   * Get stats for a specific stream
   */
  async getStreamStats(streamId: string, days: number = 30): Promise<StreamStats> {
    return api.get<StreamStats>(`/analytics/stream/${streamId}?days=${days}`);
  }
};

export default analyticsService;
